import React from 'react';
import {
  Edit2,
  Calendar,
  CalendarClock,
  CheckCircle2,
  XCircle,
  DollarSign,
  UserPlus,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext.tsx';

export type FunnelStage = 'lead' | 'agendado' | 'compareceu' | 'faltou' | 'venda' | 'paciente' | 'contato' | string;

interface LeadActionsBarProps {
  funnelStage?: FunnelStage;
  onEditLead?: () => void;
  onCreateLead?: () => void;
  onSchedule?: () => void;
  onReschedule?: () => void;
  onMarkAttendance?: () => void;
  onMarkNoShow?: () => void;
  onMarkSale?: () => void;
}

interface ActionDef {
  key: string;
  label: string;
  icon: any;
  onClick?: () => void;
  primary?: boolean;
}

const LeadActionsBar: React.FC<LeadActionsBarProps> = ({
  funnelStage,
  onEditLead,
  onCreateLead,
  onSchedule,
  onReschedule,
  onMarkAttendance,
  onMarkNoShow,
  onMarkSale,
}) => {
  const { isDark } = useTheme();

  const stageActions: ActionDef[] = (() => {
    switch (funnelStage) {
      case 'lead':
        return [
          { key: 'schedule', label: 'Agendar', icon: Calendar, onClick: onSchedule, primary: true },
        ];
      case 'agendado':
        return [
          { key: 'attendance', label: 'Compareceu', icon: CheckCircle2, onClick: onMarkAttendance, primary: true },
          { key: 'noshow', label: 'Faltou', icon: XCircle, onClick: onMarkNoShow },
          { key: 'reschedule', label: 'Reagendar', icon: CalendarClock, onClick: onReschedule },
        ];
      case 'compareceu':
        return [
          { key: 'sale', label: 'Marcar venda', icon: DollarSign, onClick: onMarkSale, primary: true },
        ];
      case 'faltou':
        return [
          { key: 'reschedule', label: 'Reagendar', icon: CalendarClock, onClick: onReschedule, primary: true },
        ];
      case 'contato':
        return [
          { key: 'create_lead', label: 'Criar lead', icon: UserPlus, onClick: onCreateLead, primary: true },
        ];
      case 'venda':
      case 'paciente':
      default:
        return [];
    }
  })();

  // "Editar" só aparece se já houver lead (não na fase 'contato')
  const baseActions: ActionDef[] = funnelStage === 'contato'
    ? []
    : [{ key: 'edit', label: 'Editar', icon: Edit2, onClick: onEditLead }];

  // Render: stage-specific actions first (primary highlighted), then base actions
  const allActions = [...stageActions, ...baseActions].filter((a) => a.onClick);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {allActions.map(({ key, label, icon: Icon, onClick, primary }) => {
        if (primary) {
          return (
            <button
              key={key}
              onClick={onClick}
              className="inline-flex items-center gap-1.5 rounded-xl bg-sminy-primary hover:bg-sminy-primary/90 px-3 py-1.5 text-xs font-medium text-white transition-colors"
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          );
        }
        return (
          <button
            key={key}
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-xs font-medium transition-colors border ${
              isDark
                ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700/40 hover:text-sminy-primary hover:border-sminy-primary/40'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-sminy-primary/5 hover:text-sminy-primary hover:border-sminy-primary/30'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default LeadActionsBar;
